import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ADMIN } from "@/constants/testIds";
import {
  adminClient,
  ADMIN_API,
  getToken,
  clearToken,
  formatApiErrorDetail,
} from "@/lib/adminApi";
import { Download, LogOut, RefreshCw, Trash2 } from "lucide-react";

export default function AdminWaitlist() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState("");

  const hasToken = !!getToken();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, listRes] = await Promise.all([
        adminClient.get("/admin/me"),
        adminClient.get("/admin/waitlist"),
      ]);
      setAdminEmail(meRes.data.email);
      setEntries(listRes.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const exportCsv = () => {
    const token = getToken();
    const url = `${ADMIN_API}/admin/waitlist.csv`;
    // Use fetch to attach auth header, then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Export failed");
        return r.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = "scrm-waitlist.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        toast.success("CSV exported.");
      })
      .catch(() => toast.error("CSV export failed."));
  };

  const removeEntry = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    try {
      await adminClient.delete(`/admin/waitlist/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entry removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed.");
    }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      data-testid={ADMIN.dashboard}
      className="min-h-screen bg-[hsl(var(--scrm-bg))] grain text-[hsl(var(--scrm-cream))]"
    >
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "hsl(var(--scrm-elevated))",
            color: "hsl(var(--scrm-cream))",
            border: "1px solid hsl(var(--scrm-cream) / 0.15)",
            borderRadius: "2px",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />

      {/* Top bar */}
      <header className="border-b hairline px-6 md:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <a
            href="/"
            className="font-display uppercase tracking-[0.04em] text-sm md:text-base"
          >
            SCRM CLUB
          </a>
          <span className="hidden sm:inline text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/45">
            · Members Console
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden md:inline text-[11px] tracking-wide text-[hsl(var(--scrm-cream))]/55 font-body">
            {adminEmail}
          </span>
          <button
            data-testid={ADMIN.dashboardLogout}
            onClick={logout}
            className="btn-ghost font-body uppercase tracking-[0.22em] text-[10px] px-4 py-2.5 inline-flex items-center gap-2"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>
      </header>

      {/* Heading */}
      <section className="px-6 md:px-10 pt-12 md:pt-16 pb-8">
        <div className="flex items-end justify-between flex-col md:flex-row gap-6">
          <div>
            <div className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-4">
              ✦ Waitlist
            </div>
            <h1
              data-testid={ADMIN.dashboardHeading}
              className="font-display uppercase tracking-mega text-4xl sm:text-5xl md:text-6xl leading-[0.92]"
            >
              The list.
            </h1>
            <div
              data-testid={ADMIN.dashboardCount}
              className="mt-4 font-body text-sm text-[hsl(var(--scrm-cream))]/65"
            >
              {loading ? "Loading…" : `${entries.length} on the list`}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid={ADMIN.dashboardRefresh}
              onClick={load}
              className="btn-ghost font-body uppercase tracking-[0.22em] text-[10px] px-5 py-3 inline-flex items-center gap-2"
            >
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
              Refresh
            </button>
            <button
              data-testid={ADMIN.dashboardExport}
              onClick={exportCsv}
              className="btn-cream font-body uppercase tracking-[0.22em] text-[10px] px-5 py-3 inline-flex items-center gap-2"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
              Export CSV
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="px-6 md:px-10 pb-24">
        {entries.length === 0 && !loading ? (
          <div
            data-testid={ADMIN.dashboardEmpty}
            className="border hairline border-[hsl(var(--scrm-cream))]/10 px-10 py-20 text-center"
          >
            <div className="font-display uppercase tracking-tight text-2xl md:text-3xl text-[hsl(var(--scrm-cream))]/80 mb-3">
              No signups yet.
            </div>
            <div className="font-body text-sm text-[hsl(var(--scrm-cream))]/50">
              When members join, they'll appear here.
            </div>
          </div>
        ) : (
          <div
            data-testid={ADMIN.dashboardTable}
            className="border hairline border-[hsl(var(--scrm-cream))]/10 overflow-x-auto"
          >
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[hsl(var(--scrm-surface))]">
                  {["#", "Name", "Email", "Phone", "Joined", ""].map((h, i) => (
                    <th
                      key={i}
                      className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 font-body font-normal px-5 py-4 border-b hairline"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr
                    key={e.id}
                    data-testid={ADMIN.dashboardRow(e.id)}
                    className="border-b hairline border-[hsl(var(--scrm-cream))]/6 hover:bg-[hsl(var(--scrm-surface))]/60 transition-colors"
                  >
                    <td className="px-5 py-4 font-display text-[hsl(var(--scrm-cream))]/35 text-sm">
                      {String(idx + 1).padStart(3, "0")}
                    </td>
                    <td className="px-5 py-4 font-body text-sm text-[hsl(var(--scrm-cream))]">
                      {e.name}
                    </td>
                    <td className="px-5 py-4 font-body text-sm text-[hsl(var(--scrm-cream))]/85">
                      <a
                        href={`mailto:${e.email}`}
                        className="hover:underline"
                      >
                        {e.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 font-body text-sm text-[hsl(var(--scrm-cream))]/70">
                      {e.phone || <span className="text-[hsl(var(--scrm-cream))]/30">—</span>}
                    </td>
                    <td className="px-5 py-4 font-body text-xs text-[hsl(var(--scrm-cream))]/55 whitespace-nowrap">
                      {formatDate(e.created_at)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        data-testid={ADMIN.dashboardDeleteBtn(e.id)}
                        onClick={() => removeEntry(e.id, e.email)}
                        aria-label={`Remove ${e.email}`}
                        className="inline-flex items-center justify-center h-8 w-8 text-[hsl(var(--scrm-cream))]/45 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
